import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import { WorkspaceNodeDrawer } from "./WorkspaceNodeDrawer";
import { WorkspaceExecutingAccountDrawerPanel } from "./WorkspaceExecutingAccountDrawerPanel";
import {
  type WorkspaceDrawerEntry,
  type WorkspaceExecutingAccountDrawerTarget,
  workspaceDrawerEntryId,
} from "./workspaceDrawerEntry";
import { WORKSPACE_OVERLAY_TRANSITION } from "./workspaceOverlayMotion";
import { cn } from "@/lib/utils";

/** Former flex gap between sheet host and pine spacer (`gap-3`). Folded into animated height. */
const WORKSPACE_DRAWER_PINE_SPACER_GAP_PX = 12;

function upsertDrawerEntry(
  prev: WorkspaceDrawerEntry[],
  entry: WorkspaceDrawerEntry
): WorkspaceDrawerEntry[] {
  const id = workspaceDrawerEntryId(entry);
  const existingIndex = prev.findIndex((item) => workspaceDrawerEntryId(item) === id);
  if (existingIndex >= 0) {
    const next = [...prev];
    next[existingIndex] = entry;
    return next;
  }
  if (prev.length < 2) return [...prev, entry];
  return [prev[1]!, entry];
}

export function useWorkspaceDrawerStack() {
  const [drawerEntries, setDrawerEntries] = useState<WorkspaceDrawerEntry[]>([]);

  const openDrawer = useCallback((node: WorkspaceNode) => {
    setDrawerEntries((prev) => upsertDrawerEntry(prev, { kind: "node", node }));
  }, []);

  const openExecutingAccountDrawer = useCallback(
    (target: WorkspaceExecutingAccountDrawerTarget) => {
      setDrawerEntries((prev) => upsertDrawerEntry(prev, { kind: "executing-account", target }));
    },
    []
  );

  const closeDrawer = useCallback((entryId: string) => {
    setDrawerEntries((prev) =>
      prev.filter((entry) => workspaceDrawerEntryId(entry) !== entryId)
    );
  }, []);

  const closeAllDrawers = useCallback(() => setDrawerEntries([]), []);

  const syncFromDetail = useCallback((detail: { nodes: WorkspaceNode[] }) => {
    setDrawerEntries((prev) => {
      if (prev.length === 0) return prev;
      return prev
        .map((entry) => {
          if (entry.kind === "executing-account") return entry;
          const node = detail.nodes.find((item) => item.id === entry.node.id);
          return node ? { kind: "node", node } : null;
        })
        .filter((entry): entry is WorkspaceDrawerEntry => entry != null);
    });
  }, []);

  const drawerNodes = useMemo(
    () =>
      drawerEntries
        .filter((entry): entry is Extract<WorkspaceDrawerEntry, { kind: "node" }> => entry.kind === "node")
        .map((entry) => entry.node),
    [drawerEntries]
  );

  return {
    drawerEntries,
    drawerNodes,
    openDrawer,
    openExecutingAccountDrawer,
    closeDrawer,
    closeAllDrawers,
    syncFromDetail,
  };
}

export type WorkspaceDrawerStackProps = {
  drawerEntries: WorkspaceDrawerEntry[];
  accountLabel?: string;
  brokerAccountCode?: string;
  brokerName?: string;
  workspaceId: string;
  nodes?: WorkspaceNode[];
  edges?: WorkspaceEdge[];
  /** Extra space above the bottom chrome inset (e.g. strategy pine dock height). */
  bottomReservePx?: number;
  /** Pine panels shown in the dock while a strategy drawer is open. */
  strategyPinePanelCount?: number;
  onClose: (entryId: string) => void;
  onNodeUpdated?: (node: WorkspaceNode) => void;
  onRefreshWorkspace?: () => Promise<void>;
};

/**
 * Pads with the same chrome inset as the floating toolbar (`p-3 md:p-4`),
 * reserves a toolbar-height band, then uses that same inset for the gap above
 * the sheet and the padding below it (toolbar↔sheet === sheet↔bottom).
 * When `bottomReservePx` is set (pine dock), a matching gap sits above that reserve
 * so the sheet and dock both fit without overlapping.
 */
export function WorkspaceDrawerStack({
  drawerEntries,
  accountLabel,
  brokerAccountCode,
  brokerName,
  workspaceId,
  nodes = [],
  edges = [],
  bottomReservePx = 0,
  strategyPinePanelCount,
  onClose,
  onNodeUpdated,
  onRefreshWorkspace,
}: WorkspaceDrawerStackProps): React.ReactElement {
  const reduceMotion = useReducedMotion();
  const [exiting, setExiting] = useState(false);
  const hadDrawersRef = useRef(false);
  /** Match live WorkspaceToolbar height (focus chips can wrap to a second row). */
  const [toolbarHeightPx, setToolbarHeightPx] = useState(48);

  useEffect(() => {
    if (drawerEntries.length > 0) {
      hadDrawersRef.current = true;
      setExiting(false);
      return;
    }
    if (hadDrawersRef.current) {
      hadDrawersRef.current = false;
      setExiting(true);
    }
  }, [drawerEntries.length]);

  useEffect(() => {
    if (drawerEntries.length === 0) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose(workspaceDrawerEntryId(drawerEntries[drawerEntries.length - 1]!));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerEntries, onClose]);

  const showChrome = drawerEntries.length > 0 || exiting;

  useEffect(() => {
    if (!showChrome) return;
    const toolbar = document.querySelector<HTMLElement>("[data-workspace-toolbar]");
    if (!toolbar) return;
    const apply = () => {
      const next = Math.ceil(toolbar.getBoundingClientRect().height);
      if (next > 0) setToolbarHeightPx(next);
    };
    apply();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(apply);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [showChrome, drawerEntries.length]);

  const drawerIsStrategy = drawerEntries.some(
    (entry) => entry.kind === "node" && entry.node.type === "strategy"
  );
  const effectiveBottomReservePx = drawerIsStrategy ? bottomReservePx : 0;
  const pineSpacerTargetPx =
    effectiveBottomReservePx > 0 && showChrome ? effectiveBottomReservePx : 0;
  const reserveBottom = pineSpacerTargetPx > 0;
  const layoutTransition = reduceMotion ? { duration: 0 } : WORKSPACE_OVERLAY_TRANSITION;

  const [pineSpacerMounted, setPineSpacerMounted] = useState(false);
  const pineSpacerTargetPxRef = useRef(pineSpacerTargetPx);
  pineSpacerTargetPxRef.current = pineSpacerTargetPx;
  useEffect(() => {
    if (pineSpacerTargetPx > 0) setPineSpacerMounted(true);
  }, [pineSpacerTargetPx]);

  const pineSpacerAnimatedHeight =
    pineSpacerTargetPx > 0
      ? pineSpacerTargetPx + WORKSPACE_DRAWER_PINE_SPACER_GAP_PX
      : 0;

  return (
    <div
      data-workspace-drawer-stack=""
      data-workspace-drawer-stack-pine-reserve={reserveBottom ? "" : undefined}
      className="pointer-events-none absolute inset-0 z-20 flex flex-col gap-3 overflow-hidden p-3 md:gap-4 md:p-4"
    >
      {showChrome ? (
        <div
          data-workspace-drawer-toolbar-spacer=""
          className="invisible shrink-0"
          aria-hidden
          style={{ height: toolbarHeightPx }}
        />
      ) : null}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          data-workspace-drawer-host=""
          data-workspace-drawer-host-count={drawerEntries.length || undefined}
          className={cn(
            "relative min-h-0 flex-1",
            drawerEntries.length > 1 && "flex flex-row justify-end gap-3"
          )}
        >
          <AnimatePresence onExitComplete={() => setExiting(false)}>
            {drawerEntries.map((entry, index) => {
              const entryId = workspaceDrawerEntryId(entry);
              const stackCount = Math.max(drawerEntries.length, 1);
              if (entry.kind === "executing-account") {
                return (
                  <WorkspaceExecutingAccountDrawerPanel
                    key={entryId}
                    target={entry.target}
                    open
                    stackIndex={index}
                    stackCount={stackCount}
                    onOpenChange={(nextOpen) => {
                      if (!nextOpen) onClose(entryId);
                    }}
                  />
                );
              }
              return (
                <WorkspaceNodeDrawer
                  key={entryId}
                  node={entry.node}
                  accountLabel={accountLabel}
                  brokerAccountCode={brokerAccountCode}
                  brokerName={brokerName}
                  workspaceId={workspaceId}
                  nodes={nodes}
                  edges={edges}
                  open
                  stackIndex={index}
                  stackCount={stackCount}
                  strategyPinePanelCount={
                    entry.node.type === "strategy" ? strategyPinePanelCount : undefined
                  }
                  onOpenChange={(nextOpen) => {
                    if (!nextOpen) onClose(entryId);
                  }}
                  onNodeUpdated={onNodeUpdated}
                  onRefreshWorkspace={onRefreshWorkspace}
                />
              );
            })}
          </AnimatePresence>
        </div>
        {pineSpacerMounted ? (
          <motion.div
            data-workspace-drawer-pine-spacer=""
            className="invisible shrink-0 overflow-hidden"
            aria-hidden
            initial={{ height: 0 }}
            animate={{ height: pineSpacerAnimatedHeight }}
            transition={layoutTransition}
            onAnimationComplete={() => {
              if (pineSpacerTargetPxRef.current <= 0) setPineSpacerMounted(false);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
